import React, { useEffect, useState } from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Select from 'react-select';

import { CellProps, useTable } from '../table/useTable';
import { AccountData, AuditLogService, AuditType, PropelUserModel, UserService } from '../api';
import { AuditLogData, AuditLogFilters, isEventAuditLog, isOrderAuditLog } from './types';
import { AuditLogChangeDetails } from './components/AuditLogChangeDetails';
import { AuditLogFlowVisualization } from './components/AuditLogFlowVisualization';
import { AuditLogEntityLink } from './components/AuditLogEntityLink';
import { getAuditTypeIcon } from './utils';
import { useAuth } from '../session/InternalAuthProvider';
import { getDateFormat, getTimeFormat } from '../common/getDateAndTimeFormat';
import RotatingLoader from '../common/ui/RotatingLoader';

export const AuditLogPage: React.FC = () => {
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [users, setUsers] = useState<PropelUserModel[]>([]);
  useEffect(() => {
    const fetchUsers = async () => {
      const users = await UserService.getUsers();
      if (setUsers) {
        setUsers(users);
      }
      setIsLoadingUsers(false);
    };
    void fetchUsers();
  }, []);

  const fetcher = async ({ search, page, perPage }: { search?: string; page: number; perPage: number }) => {
    let orderNumber: number | undefined;

    if (search) {
      const parsedOrderNumber = parseInt(search, 10);
      if (!Number.isNaN(parsedOrderNumber)) {
        orderNumber = parsedOrderNumber;
      }
    }

    try {
      const response = await AuditLogService.getAuditLogs(
        filters.auditTypes,
        undefined,
        orderNumber,
        filters.startDate,
        filters.endDate,
        page,
        perPage,
      );
      return response as AuditLogData[];
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  };

  const getAuditTypeLabel = (auditType: AuditType): string => {
    return t(`auditlog.audit_types.${auditType}`);
  };

  const tableDetails: CellProps<AuditLogData>[] = [
    {
      id: 'timestamp',
      alignment: 'left',
      label: t('auditlog.table.columns.time'),
      render: (log: AuditLogData) => (
        <Typography variant="body2">
          {moment(log.created_at).format(`${getDateFormat(account).longDateFormat}  ${getTimeFormat(account)}`)}
        </Typography>
      ),
    },
    {
      id: 'type',
      alignment: 'justify',
      label: t('auditlog.table.columns.action'),
      render: (log: AuditLogData) => (
        <Box display="flex" gap={1} justifyContent="flex-start">
          <span style={{ fontSize: '16px' }}>{getAuditTypeIcon(log.audit_type)}</span>
          <Typography variant="body2">{getAuditTypeLabel(log.audit_type)}</Typography>
        </Box>
      ),
    },
    {
      id: 'entity',
      alignment: 'left',
      label: t('auditlog.table.columns.entity'),
      render: (log: AuditLogData) => <AuditLogEntityLink log={log} />,
    },
    {
      id: 'user',
      alignment: 'left',
      label: t('auditlog.table.columns.user'),
      render: (log: AuditLogData) => {
        const user = users.find((user: PropelUserModel) => user.user_id === log.user_id);
        return (
          <Typography noWrap>
            {user
              ? `${user.first_name} ${user.last_name}`
              : log.user_id
              ? t('auditlog.mbuser')
              : t('auditlog.customer')}
          </Typography>
        );
      },
    },
    {
      id: 'changes',
      alignment: 'left',
      label: t('auditlog.table.columns.changes'),
      render: (log: AuditLogData) => {
        if (isOrderAuditLog(log) && log.changes.length > 0) {
          return <AuditLogChangeDetails changes={log.changes} users={users} />;
        }
        if (isEventAuditLog(log) && log.changes.length > 0) {
          return <AuditLogChangeDetails changes={log.changes} users={users} />;
        }
        return <Typography variant="body2">-</Typography>;
      },
    },
    {
      id: 'flow',
      alignment: 'center',
      label: t('auditlog.table.columns.flow'),
      render: (log: AuditLogData) => {
        if (log.flow_chain_id) {
          return <AuditLogFlowVisualization flowChainId={log.flow_chain_id} users={users} />;
        }
        return <Typography variant="body2">-</Typography>;
      },
    },
  ];

  const auditTypeOptions = Object.values(AuditType).map((type) => ({
    value: type,
    label: `${getAuditTypeIcon(type)} ${getAuditTypeLabel(type)}`,
  }));

  const { TableComponent, isLoadingData } = useTable<AuditLogData>({
    opId: `getAuditLogs-${JSON.stringify(filters)}`,
    fetcher,
    tableDetails,
    actionsComponent: (
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Select
            closeMenuOnSelect={false}
            isMulti
            options={auditTypeOptions}
            value={auditTypeOptions.filter((option) => filters.auditTypes?.includes(option.value))}
            onChange={(selectedOptions) => {
              const selectedTypes = selectedOptions ? (selectedOptions as any[]).map((option) => option.value) : [];
              setFilters({ ...filters, auditTypes: selectedTypes });
            }}
            placeholder={<Typography>{t('auditlog.filters.all_types')}</Typography>}
            styles={{
              // @ts-ignore
              control: (base) => ({
                ...base,
                minHeight: '40px',
                maxWidth: '300px', // Set maximum width
                minWidth: '200px',
              }),
              // @ts-ignore
              valueContainer: (base) => ({
                ...base,
                maxWidth: '250px', // Slightly less than control width
                overflow: 'hidden',
              }),
              // @ts-ignore
              multiValue: (base) => ({
                ...base,
                maxWidth: '100px', // Limit individual tag width
                overflow: 'hidden',
              }),
              // @ts-ignore
              multiValueLabel: (base) => ({
                ...base,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }),
              // @ts-ignore
              menu: (base) => ({
                ...base,
                zIndex: 9999,
                minWidth: '100px',
              }),
              // @ts-ignore
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999,
              }),
              // @ts-ignore
              menuList: (base) => ({
                ...base,
                maxHeight: 'none',
                overflow: 'visible',
              }),
            }}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={3}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label={t('auditlog.filters.start_date')}
              value={filters.startDate ? moment(filters.startDate) : null}
              onChange={(date) => setFilters({ ...filters, startDate: date?.format('YYYY-MM-DD') })}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6} sm={3} md={3}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label={t('auditlog.filters.end_date')}
              value={filters.endDate ? moment(filters.endDate) : null}
              onChange={(date) => setFilters({ ...filters, endDate: date?.format('YYYY-MM-DD') })}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
    ),
  });

  return (
    <Grid container p={2}>
      <Grid item xs={12}>
        <Typography variant="h4" gutterBottom>
          {t('auditlog.page.title')}
        </Typography>
      </Grid>
      {isLoadingData || isLoadingUsers ? (
        <Grid
          container
          sx={{
            minHeight: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <RotatingLoader />
        </Grid>
      ) : (
        <Grid item xs={12}>
          <Paper elevation={1}>{TableComponent}</Paper>
        </Grid>
      )}
    </Grid>
  );
};
